import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  IconButton,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import BoothChip from '../common/BoothChip';
import FinalConfirmationEmailDialog from './FinalConfirmationEmailDialog';
import { useFestival } from '../../context/FestivalContext';
import { formatCurrency, groupFinalRegistrations } from '../../utils/finalRegistrations';

const formatBoothLabel = (booth) => {
  const parts = [booth.booth_number];
  if (booth.is_corner) parts.push('Corner');
  if (booth.needs_electricity) parts.push('Electricity');
  return parts.filter(Boolean).join(' • ');
};

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = [headers, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const FinalRegistrations = () => {
  const navigate = useNavigate();
  const { selectedId: festivalId } = useFestival();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  useEffect(() => {
    fetchFinalRegistrations();
  }, [festivalId]);

  const fetchFinalRegistrations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/final-registrations');
      setRegistrations(response.data);
    } catch (err) {
      console.error('Error fetching final registrations:', err);
      setError('Failed to fetch final registrations');
    } finally {
      setLoading(false);
    }
  };

  const vendorRows = useMemo(() => groupFinalRegistrations(registrations), [registrations]);

  const handleExportCSV = () => {
    const headers = ['Business', 'Booths', 'Contact', 'Email', 'Phone', 'Amount Paid'];
    const rows = vendorRows.map((vendor) => {
      const contact = `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim() || '—';
      return [
        vendor.business_name || '',
        vendor.booths.map(formatBoothLabel).join(', '),
        contact,
        vendor.email || '',
        vendor.phone || 'No phone',
        formatCurrency(vendor.amount_paid_cents),
      ];
    });
    downloadCsv('final-registrations.csv', headers, rows);
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/admin')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Final Registration Status
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={vendorRows.length === 0}
            >
              Download CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => navigate('/admin/final-registrations/print')}
              disabled={vendorRows.length === 0}
            >
              Print Vendor Sheets
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              onClick={() => setEmailDialog(true)}
              disabled={vendorRows.length === 0}
            >
              Send Final Confirmation Emails
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {emailSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {emailSuccess}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 4 },
              alignItems: 'center',
              py: 2,
              '&:last-child': { pb: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" component="span" fontWeight="medium">
                {vendorRows.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vendorRows.length === 1 ? 'Vendor' : 'Vendors'}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" component="span" fontWeight="medium">
                {registrations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {registrations.length === 1 ? 'Booth' : 'Booths'}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" component="span" fontWeight="medium">
                {formatCurrency(
                  vendorRows.reduce((sum, v) => sum + v.amount_paid_cents, 0)
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Collected
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Business</TableCell>
                  <TableCell>Booths</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="right">Amount Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No complete registrations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  vendorRows.map((vendor) => (
                    <TableRow key={vendor.vendor_id ?? vendor.email}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {vendor.business_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          {vendor.booths.map((booth) => (
                            <BoothChip
                              key={booth.booth_number}
                              name={booth.booth_number}
                              isCorner={booth.is_corner}
                              needsElectricity={booth.needs_electricity}
                              size="small"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {`${vendor.first_name || ''} ${vendor.last_name || ''}`.trim() || '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vendor.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vendor.phone || 'No phone'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(vendor.amount_paid_cents)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <FinalConfirmationEmailDialog
          open={emailDialog}
          onClose={() => setEmailDialog(false)}
          onSent={setEmailSuccess}
        />
      </Container>
  );
};

export default FinalRegistrations;
